import Head from 'next/head'
import classNames from 'classnames'
import parse from 'html-react-parser'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next/pages'
import type { CareersDetailProps } from './types'
import styles from './CareersDetail.module.scss'
import { generateJobPostingStructuredData } from 'utils/structuredData'
import { routes } from 'utils/routes'
import { Heading, Header } from '@boltenergy-be/design-system'
import Link from 'components/link/Link'
import BlocksRenderer from 'components/blocks-renderer/BlocksRenderer'
import { useGetLanguage } from 'hooks/useGetLanguage'

const CareersDetail = ({ jobOffer }: CareersDetailProps) => {
  // Next router
  const router = useRouter()

  // i18n
  const { t } = useTranslation(['common', 'careers'])
  const language = useGetLanguage()

  return (
    <>
      {/* SEO */}
      <Head>
        <script type="application/ld+json" key="job-posting-jsonld" dangerouslySetInnerHTML={generateJobPostingStructuredData(jobOffer)} />
      </Head>

      <main className={styles['career-detail']}>
        {/* HERO */}
        <Header docked title={jobOffer.title} color="accent-soft">
          <strong className="h5 serif">{parse(t('careers:vacancy', 'Vacature'))}</strong>
        </Header>

        {/* CAREER CONTENT */}
        <section className={classNames('section', styles['career-content'])}>
          <div className={classNames('container', styles.container)}>
            <button onClick={() => router.push(routes(language).careers)} className={styles['back-button']}>
              &laquo; {t('back', 'Ga terug')}
            </button>
            <Heading as="h2" variant="h3">
              {jobOffer.title}
            </Heading>
            <div>{jobOffer.content ? <BlocksRenderer content={jobOffer.content} /> : parse(jobOffer.description ?? '')}</div>
            {jobOffer.mailTo && (
              <>
                <Link
                  as="a"
                  representation="button"
                  className="mt-300"
                  href={`mailto:${jobOffer.mailTo}?subject=${t('careers:vacancy', 'Vacature')} ${jobOffer.title}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {jobOffer?.apply || t('careers:applyNow', 'Solliciteer nu')}
                </Link>

                {jobOffer?.closingText ? (
                  <BlocksRenderer content={jobOffer.closingText} />
                ) : (
                  <p>{parse(t('careers:emailTo', { email: jobOffer.mailTo }))}</p>
                )}
              </>
            )}
          </div>
        </section>
      </main>
    </>
  )
}

export default CareersDetail
